import { Typography } from '@mui/material';
import { MouseEvent } from 'react';

type UsageLine = {
  count?: number | null;
  singular: string;
  plural: string;
  /** Force muted styling; otherwise Booking/Bookings lines are muted automatically. */
  muted?: boolean;
  /** Called when count > 0. Click is stopped from bubbling to the row. */
  onClick?: (event: MouseEvent) => void;
};

const isBookingUsageLine = (line: UsageLine) => {
  if (typeof line.muted === 'boolean') return line.muted;
  return /booking/i.test(line.singular) || /booking/i.test(line.plural);
};

export const formatUsageCell = (lines: UsageLine[]) => (
  <>
    {lines.map(line => {
      const count = Number(line.count ?? 0);
      const label = count === 1 ? line.singular : line.plural;
      const bookingLine = isBookingUsageLine(line);
      const isClickable = Boolean(line.onClick) && count > 0;

      return (
        <Typography
          key={line.plural}
          component={isClickable ? 'button' : 'p'}
          type={isClickable ? 'button' : undefined}
          onClick={
            isClickable
              ? (event: MouseEvent) => {
                  event.stopPropagation();
                  line.onClick?.(event);
                }
              : undefined
          }
          sx={
            bookingLine
              ? {
                  display: 'block',
                  border: 0,
                  background: 'none',
                  p: 0,
                  m: 0,
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  lineHeight: 1.35,
                  color: 'text.secondary',
                }
              : {
                  display: 'block',
                  border: 0,
                  background: 'none',
                  p: 0,
                  m: 0,
                  textAlign: 'left',
                  fontSize: '0.8125rem',
                  fontWeight: isClickable ? 700 : 500,
                  lineHeight: 1.35,
                  color: isClickable ? '#175CD3' : '#344054',
                  textDecoration: isClickable ? 'underline' : 'none',
                  textUnderlineOffset: '2px',
                  cursor: isClickable ? 'pointer' : 'default',
                  '&:hover': isClickable ? { color: '#0B4AAB' } : undefined,
                }
          }>
          {count} {label}
        </Typography>
      );
    })}
  </>
);

export const usageTableColumn = (getLines: (row: any) => UsageLine[], headerName = 'Used In') => ({
  field: 'usage',
  headerName,
  flex: 2,
  sortable: false,
  renderCell: (_value: any, row: any) => formatUsageCell(getLines(row)),
});
